import React, { useState, useEffect, useCallback, useRef } from 'react';
import AdminBar from './adminBar.jsx';
import supabase from '../../supabase.js'; 
import { Helmet } from 'react-helmet-async';

export default function Reports() {
    const [loading, setLoading] = useState(true); 
    const [allReviewGroups, setAllReviewGroups] = useState([]); 
    const isInitialLoadComplete = useRef(false);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            // ONE JOIN to rule them all: Get reports AND review details in 1 query
            const { data, error } = await supabase
                .from('reports')
                .select(`
                    id, 
                    reason, 
                    reporter_user_id, 
                    created_at, 
                    review:review_id (id, title, text, author, company, stars, status)
                `)
                .eq('moderation_status', 'pending');

            if (error) throw error;

            // Grouping logic: Grouping multiple reports under one review ID
            const groups = data.reduce((acc, report) => {
                const r = report.review;
                if (!r) return acc;
                
                if (!acc[r.id]) {
                    acc[r.id] = {
                        reviewDetails: r,
                        reports: [],
                        totalReports: 0
                    };
                }
                acc[r.id].reports.push(report);
                acc[r.id].totalReports++;
                return acc;
            }, {});

            // Sort by most reported first
            const sortedGroups = Object.values(groups).sort((a, b) => b.totalReports - a.totalReports);
            setAllReviewGroups(sortedGroups);
        } catch (err) {
            console.error("Fetch error:", err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isInitialLoadComplete.current) {
            isInitialLoadComplete.current = true;
            loadAll();
        }
    }, [loadAll]);

    const handleIgnore = async (reviewId) => {
        // Optimistic UI update
        setAllReviewGroups(prev => prev.filter(g => g.reviewDetails.id !== reviewId));
        await supabase.from('reports').update({ moderation_status: 'ignored' }).eq('review_id', reviewId);
    };

    const handleRemove = async (review) => {
        if (!review) return;
        if (!window.confirm(`Are you sure you want to PERMANENTLY delete this review for ${review.company}?`)) return;

        setLoading(true);
        try {
            // 1. Mark reports as processed
            await supabase.from('reports').update({ moderation_status: 'testimonial_removed' }).eq('review_id', review.id);
            
            // 2. Delete the actual review
            const { error: deleteError } = await supabase.from('reviews').delete().eq('id', review.id);
            if (deleteError) throw deleteError;

            // 3. Update the company metrics (bank or businesses table)
            let targetTable = 'bank';
            let { data: company } = await supabase.from('bank').select('*').eq('www', review.company).maybeSingle();

            if (!company) {
                const { data: bizData } = await supabase.from('businesses').select('*').eq('www', review.company).maybeSingle();
                if (bizData) {
                    company = bizData;
                    targetTable = 'businesses';
                }
            }

            if (company) {
                let updatePayload = {};
                const currentReviews = Number(company.reviews || 0);
                const currentScore = Number(company.trustscore || 0);
                const reviewStars = Number(review.stars || 0);

                if (currentReviews > 1) {
                    const newCount = currentReviews - 1;
                    const newScore = ((currentScore * currentReviews) - reviewStars) / newCount;
                    updatePayload = {
                        trustscore: parseFloat(newScore.toFixed(2)),
                        reviews: newCount
                    };
                } else {
                    updatePayload = { trustscore: 0, reviews: 0 };
                }

                // Handle legacy 'bank' status resets if applicable
                if (targetTable === 'bank') {
                    updatePayload.SG = '';
                    updatePayload.SGFOUR = '';
                }

                await supabase.from(targetTable).update(updatePayload).eq('www', review.company);
            }
            
            setAllReviewGroups(prev => prev.filter(g => g.reviewDetails.id !== review.id));
        } catch (err) {
            console.error("Moderation error:", err);
            alert("Update failed: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading && allReviewGroups.length === 0) {
        return <div className='flex items-center justify-center min-h-screen font-poppins text-slate-500'>Syncing Moderation Database...</div>;
    }

    return (
        <div className='min-h-screen bg-slate-50 font-poppins'>
                   <Helmet>
                    <title>Admin | Sitemanifest</title> 
                    
                    <meta name="robots" content="noindex, nofollow" />
                    
                    <meta name="googlebot" content="noarchive" />
                  </Helmet>
            <AdminBar />
            
            <div className='pt-[140px] px-6 max-w-6xl mx-auto pb-20'>
                {/* Header Area */}
                <div className='flex items-end justify-between mb-10 border-b border-slate-200 pb-6'>
                    <div>
                        <h2 className='text-3xl font-bold text-slate-900'>Moderation Queue</h2>
                        <p className='text-slate-500 mt-1'>Review flagged content and maintain platform integrity.</p>
                    </div>
                    <div className='bg-red-50 text-red-600 px-4 py-2 rounded-lg font-bold text-sm border border-red-100'>
                        {allReviewGroups.length} Reviews Flagged
                    </div>
                </div>

                {allReviewGroups.length === 0 ? (
                    <div className='bg-white rounded-2xl p-20 text-center border-2 border-dashed border-slate-200'>
                        <p className='text-slate-400 font-medium'>The queue is empty. Good job!</p>
                    </div>
                ) : (
                    <div className='space-y-8'>
                        {allReviewGroups.map((group) => {
                            const r = group.reviewDetails;
                            return (
                                <div key={r.id} className='bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden transition-all hover:shadow-md'>
                                    {/* Sub-header with Review Metadata */}
                                    <div className='px-6 py-4 bg-slate-50 border-b border-slate-100 flex justify-between items-center'>
                                        <div className='flex items-center gap-4'>
                                            <span className='font-bold text-slate-900'>{r.company}</span>
                                            <span className='text-slate-300'>|</span>
                                            <span className='text-xs font-bold px-2 py-0.5 bg-white border border-slate-200 rounded text-slate-600'>{r.stars} Stars</span>
                                            <span className='text-xs text-slate-400'>Author: {r.author}</span>
                                        </div>
                                        <div className='flex items-center gap-2 text-red-600'>
                                            <span className='w-2 h-2 bg-red-600 rounded-full animate-pulse'></span>
                                            <span className='text-xs font-black uppercase tracking-wider'>{group.totalReports} Reports</span>
                                        </div>
                                    </div>

                                    <div className='grid lg:grid-cols-2 divide-x divide-slate-100'>
                                        {/* Left Side: The Review Content */}
                                        <div className='p-6'>
                                            <h4 className='font-bold text-slate-800 text-lg mb-2'>{r.title}</h4>
                                            <p className='text-slate-600 text-sm leading-relaxed bg-slate-50 p-4 rounded-xl italic border border-slate-100'>
                                                "{r.text}"
                                            </p>
                                        </div>

                                        {/* Right Side: Reasons list */}
                                        <div className='p-6 bg-slate-50/30'>
                                            <h4 className='text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4'>Flag Reasons</h4>
                                            <div className='space-y-3 max-h-[200px] overflow-y-auto pr-2 custom-scrollbar'>
                                                {group.reports.map((rep) => (
                                                    <div key={rep.id} className='bg-white p-3 rounded-lg border border-red-50 shadow-sm'>
                                                        <p className='text-sm text-slate-800 font-medium leading-tight'>"{rep.reason}"</p>
                                                        <p className='text-[9px] text-slate-400 mt-2 uppercase font-bold tracking-tighter'>
                                                            Reporter ID: {rep.reporter_user_id.slice(0, 12)}...
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Bar */}
                                    <div className='px-6 py-4 bg-white border-t border-slate-100 flex justify-end gap-3'>
                                        <button 
                                            onClick={() => handleIgnore(r.id)}
                                            className='px-5 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg transition-colors'
                                        >
                                            Dismiss Reports
                                        </button>
                                        <button 
                                            onClick={() => handleRemove(r)}
                                            disabled={loading}
                                            className='px-5 py-2 text-xs font-bold bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-sm shadow-red-200 disabled:opacity-50'
                                        >
                                            {loading ? 'Processing...' : 'Delete Review & Update Scores'}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}